import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import SEOHead from '../components/SEO/SEOHead';

// Post detail page with replies, like, mark-solved, and delete options
export default function CommunityDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [post, setPost] = useState(null);
  const [replies, setReplies] = useState([]);

  const [replyText, setReplyText] = useState('');
  const [replyLoading, setReplyLoading] = useState(false);
  const [likeLoading, setLikeLoading] = useState(false);

  const fetchDetail = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.community.getPost(id);
      setPost(res.data.post);
      setReplies(res.data.replies || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load post');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const addReply = async (e) => {
    e.preventDefault();
    if (!user) return navigate('/login');
    if (!replyText.trim()) return;
    setReplyLoading(true);
    setError('');
    try {
      await api.community.addReply(id, { content: replyText.trim() });
      setReplyText('');
      await fetchDetail();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add reply');
    } finally {
      setReplyLoading(false);
    }
  };

  const deleteReply = async (replyId) => {
    if (!user) return navigate('/login');
    if (!window.confirm('Delete this reply?')) return;
    try {
      await api.community.deleteReply(replyId);
      setReplies(prev => prev.filter(r => r.id !== replyId));
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete reply');
    }
  };

  const toggleLike = async () => {
    if (!user) return navigate('/login');
    setLikeLoading(true);
    try {
      const res = await api.community.toggleLike(id);
      setPost(p => p ? { ...p, likes_count: res.data.likes_count } : p);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to toggle like');
    } finally {
      setLikeLoading(false);
    }
  };

  const markSolved = async () => {
    if (!user) return navigate('/login');
    if (!window.confirm('Mark this post as solved?')) return;
    try {
      await api.community.markSolved(id);
      await fetchDetail();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to mark as solved');
    }
  };

  const deletePost = async () => {
    if (!user) return navigate('/login');
    if (!window.confirm('Delete this post?')) return;
    try {
      await api.community.deletePost(id);
      navigate('/community');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete post');
    }
  };

  const canModerate = (entityUserId) => {
    if (!user) return false;
    return user.role === 'admin' || user.id === entityUserId;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <Link to="/community" className="px-4 py-2 text-blue-600 hover:text-blue-700 font-medium">← Back to Community</Link>
            <div className="flex gap-4">
              <Link to="/help-center" className="px-4 py-2 text-gray-600 hover:text-blue-600 text-sm">Help Center</Link>
              <Link to="/" className="px-4 py-2 text-blue-600 hover:text-blue-700 font-medium">Home</Link>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-4 p-3 rounded bg-red-50 text-red-700 border border-red-200">{error}</div>
        )}

        {loading ? (
          <div className="p-6 text-center text-gray-600">Loading...</div>
        ) : post ? (
          <div className="space-y-6">
            {/* SEO for post page */}
            <SEOHead
              title={`${post.title} | Community | FBR Digital Invoicing`}
              description={(post.content || '').slice(0, 150)}
              url={`/community/${post.id}`}
              keywords={Array.isArray(post.tags) ? post.tags : []}
            />
            <div className="bg-white rounded-xl shadow p-6">
              <div className="flex items-start justify-between">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">{post.title}</h1>
                  <div className="mt-2 text-sm text-gray-500">
                    <span className="mr-2">By {post.author_name || 'User'}</span>
                    <span className="mr-2">• {post.category}</span>
                    <span className="mr-2">• {post.replies_count || 0} replies</span>
                    <span className="mr-2">• {post.likes_count || 0} likes</span>
                    <span>• {post.views_count || 0} views</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 text-xs rounded-full border ${post.status === 'solved' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-gray-50 text-gray-700 border-gray-200'}`}>
                    {post.status}
                  </span>
                </div>
              </div>
              <div className="prose max-w-none mt-4 whitespace-pre-wrap">{post.content}</div>

              <div className="mt-6 flex items-center gap-3">
                <button onClick={toggleLike} disabled={likeLoading} className="px-4 py-2 rounded-lg border bg-white disabled:opacity-50">
                  👍 Like ({post.likes_count || 0})
                </button>
                {canModerate(post.user_id) && post.status !== 'solved' && (
                  <button onClick={markSolved} className="px-4 py-2 rounded-lg bg-green-600 text-white">Mark Solved</button>
                )}
                {canModerate(post.user_id) && (
                  <button onClick={deletePost} className="px-4 py-2 rounded-lg bg-red-600 text-white">Delete</button>
                )}
              </div>
            </div>

            <div className="bg-white rounded-xl shadow p-6">
              <h2 className="text-lg font-semibold mb-4">Replies</h2>
              {replies.length === 0 ? (
                <p className="text-gray-600">No replies yet.</p>
              ) : (
                <ul className="space-y-4">
                  {replies.map(r => (
                    <li key={r.id} className="border rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="text-sm text-gray-600 mb-1">{r.author_name || 'User'}</div>
                          <div className="whitespace-pre-wrap">{r.content}</div>
                        </div>
                        {canModerate(r.user_id) && (
                          <button onClick={() => deleteReply(r.id)} className="text-red-600 hover:underline">Delete</button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}

              <form onSubmit={addReply} className="mt-6 space-y-3">
                <textarea
                  className="w-full border rounded-lg px-3 py-2"
                  rows={4}
                  placeholder={user ? 'Write a reply...' : 'Login to reply'}
                  value={replyText}
                  onChange={e => setReplyText(e.target.value)}
                  disabled={!user}
                />
                <div className="flex items-center justify-end">
                  <button type="submit" disabled={!user || replyLoading} className="px-4 py-2 rounded-lg bg-blue-600 text-white disabled:opacity-50">
                    {replyLoading ? 'Posting...' : 'Reply'}
                  </button>
                </div>
              </form>
            </div>

            {/* Related Resources Section - SEO Links */}
            <div className="bg-white rounded-xl shadow p-6 mt-6">
              <h2 className="text-lg font-semibold mb-4">Related Resources</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="border rounded-lg p-4 hover:bg-blue-50 transition">
                  <h3 className="font-semibold text-gray-900 mb-2">📚 FBR API Documentation</h3>
                  <p className="text-sm text-gray-600 mb-3">Learn how to integrate with the FBR Digital Invoicing API</p>
                  <Link to="/help-center" className="text-blue-600 hover:text-blue-700 text-sm font-medium">View Documentation →</Link>
                </div>
                <div className="border rounded-lg p-4 hover:bg-blue-50 transition">
                  <h3 className="font-semibold text-gray-900 mb-2">🎓 Training & Guides</h3>
                  <p className="text-sm text-gray-600 mb-3">Step-by-step guides for invoice submission and management</p>
                  <Link to="/training" className="text-blue-600 hover:text-blue-700 text-sm font-medium">Explore Guides →</Link>
                </div>
                <div className="border rounded-lg p-4 hover:bg-blue-50 transition">
                  <h3 className="font-semibold text-gray-900 mb-2">💼 Use Cases & Features</h3>
                  <p className="text-sm text-gray-600 mb-3">Real-world scenarios and integration options</p>
                  <Link to="/free-fbr-digital-invoicing" className="text-blue-600 hover:text-blue-700 text-sm font-medium">View Features →</Link>
                </div>
                <div className="border rounded-lg p-4 hover:bg-blue-50 transition">
                  <h3 className="font-semibold text-gray-900 mb-2">🔗 More Discussions</h3>
                  <p className="text-sm text-gray-600 mb-3">Continue exploring community discussions and solutions</p>
                  <Link to="/community" className="text-blue-600 hover:text-blue-700 text-sm font-medium">View All Posts →</Link>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="p-6 text-center text-gray-600">Post not found.</div>
        )}
      </div>
    </div>
  );
}